import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, CircularProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Link, Tabs, Tab, Card, CardContent,
  LinearProgress, Button, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment,
  Pagination, useTheme, useMediaQuery, Tooltip
} from '@mui/material';
import {
  PlayArrow, Devices, Movie, Tv, History, Refresh, OpenInNew, Storage, Search, Schedule, PlayCircle,
  Storage as DiskIcon, CheckCircle as OnlineIcon, Error as OfflineIcon
} from '@mui/icons-material';
import { BarChart3, Clock, HardDrive } from 'lucide-react';
import axios from 'axios';
import { styled, alpha, keyframes } from '@mui/material/styles';
import { fadeInUp, GlassmorphicCard, SectionTitle, BodyText, SecondaryText, CardTitle, CaptionText, PrimaryButton } from '../styles/sharedStyles';

const SummaryCard = styled(GlassmorphicCard)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.4),
  border: '1px solid rgba(255, 255, 255, 0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
    background: alpha(theme.palette.background.paper, 0.6),
  },
}));

const JellyfinDiskCard = styled(GlassmorphicCard)(({ theme }) => ({
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  background: alpha(theme.palette.background.paper, 0.4),
  border: '1px solid rgba(255, 255, 255, 0.05)',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  '&:hover': {
    transform: 'translateY(-8px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
    background: alpha(theme.palette.background.paper, 0.6),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #00d4ff, #00ff88)',
    opacity: 0.5,
  }
}));

const MediaCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '16px',
  overflow: 'hidden',
  aspectRatio: '2/3',
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.4)',
    borderColor: 'rgba(0, 212, 255, 0.3)',
    '& .media-overlay': {
      opacity: 1,
    }
  }
}));

const MediaOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
  opacity: 0.8,
  transition: 'opacity 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
}));

const DiskStatusChip = styled(Chip)(({ status }) => {
  const isOnline = status === 'ONLINE' || status === 'online' || status === true;
  return {
    backgroundColor: isOnline ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 68, 68, 0.1)',
    color: isOnline ? '#00ff88' : '#ff4444',
    border: `1px solid ${isOnline ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 68, 68, 0.2)'}`,
    fontWeight: 700,
    fontSize: '0.65rem',
    height: '20px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    '& .MuiChip-icon': {
      color: 'inherit',
      fontSize: '12px',
    }
  };
});

const UsageProgress = styled(LinearProgress)(({ theme, value, color_theme = 'blue' }) => {
  let gradient;
  if (value > 90) {
    gradient = 'linear-gradient(90deg, #ff4444, #ff6b6b)';
  } else if (value > 75) {
    gradient = 'linear-gradient(90deg, #ffc107, #ffaa00)';
  } else {
    gradient = color_theme === 'blue' 
      ? 'linear-gradient(90deg, #00d4ff, #0099ff)'
      : color_theme === 'orange'
      ? 'linear-gradient(90deg, #ff9800, #f57c00)'
      : 'linear-gradient(90deg, #00ff88, #00d4ff)';
  }

  return {
    height: 10,
    borderRadius: 5,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    '& .MuiLinearProgress-bar': {
      borderRadius: 5,
      background: gradient,
      boxShadow: value > 90 ? '0 0 10px rgba(255, 68, 68, 0.5)' : 'none',
    },
  };
});

const IconWrapper = styled(Box)(({ theme, color = '156, 39, 176' }) => ({
  width: 48,
  height: 48,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1.5),
  background: `rgba(${color}, 0.15)`,
  color: `rgb(${color})`,
  border: `1px solid rgba(${color}, 0.3)`,
  '&:hover': { background: `rgba(${color}, 0.25)` },
}));

// Mobile: horizontal scroll for quick stats — same left margin as disk pools
const SummaryScrollRow = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    gap: theme.spacing(1.5),
    overflowX: 'auto',
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    margin: theme.spacing(0, -2),
    marginLeft: theme.spacing(2),
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
    '&::-webkit-scrollbar': { display: 'none' },
    '& > *': {
      flex: '0 0 42%',
      scrollSnapAlign: 'start',
      minWidth: '100px',
      maxWidth: '140px',
    },
  },
}));

// Compact summary card for mobile scroll
const CompactSummaryCard = styled(GlassmorphicCard)(({ theme }) => ({
  padding: theme.spacing(1.25),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minHeight: 72,
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  transition: 'all 0.2s ease',
  '&:hover': { border: '1px solid rgba(255, 255, 255, 0.12)' },
}));

// Mobile: horizontal scroll for disk cards + total storage — same left margin as disk pools
const DiskCardsScrollRow = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    gap: theme.spacing(2),
    overflowX: 'auto',
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    margin: theme.spacing(0, -2),
    marginLeft: theme.spacing(2),
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
    '&::-webkit-scrollbar': { display: 'none' },
    '& > *': {
      flex: '0 0 85%',
      scrollSnapAlign: 'start',
      minWidth: '280px',
    },
  },
}));

// Mobile: horizontal scroll for Recently Added — same left margin as disk pools
const RecentlyAddedScrollRow = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    gap: theme.spacing(2),
    overflowX: 'auto',
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    margin: theme.spacing(0, -2),
    marginLeft: theme.spacing(2),
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
    '&::-webkit-scrollbar': { display: 'none' },
    '& > *': {
      flex: '0 0 45%',
      scrollSnapAlign: 'start',
      minWidth: '140px',
    },
  },
}));

// Mobile: horizontal scroll for session cards — same left margin as disk pools
const SessionCardsScrollRow = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    gap: theme.spacing(2),
    overflowX: 'auto',
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    margin: theme.spacing(0, -2),
    marginLeft: theme.spacing(2),
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
    '&::-webkit-scrollbar': { display: 'none' },
    '& > *': {
      flex: '0 0 85%',
      scrollSnapAlign: 'start',
      minWidth: '260px',
    },
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.02)',
  backdropFilter: 'blur(10px)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  marginTop: theme.spacing(3),
  overflow: 'hidden',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.85)',
    padding: theme.spacing(2, 2),
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    fontWeight: 700,
    color: '#00d4ff',
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    letterSpacing: '1px',
  },
  '& .MuiTableRow-root:hover': { backgroundColor: 'rgba(255, 255, 255, 0.02)' },
}));

// Jellyfin returns PascalCase; support both
const get = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    const v = obj[k] ?? obj[k.replace(/([A-Z])/g, (m) => m.toLowerCase())];
    if (v !== undefined) return v;
  }
  return undefined;
};

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status'
})(({ status }) => {
  let bgColor, textColor, borderColor;

  const statusStr = String(status || 'unknown').toLowerCase();

  switch (statusStr) {
    case 'running':
    case 'active':
    case 'enabled':
    case 'true':
      bgColor = 'rgba(0, 255, 136, 0.1)';
      textColor = '#00ff88';
      borderColor = 'rgba(0, 255, 136, 0.3)';
      break;
    case 'stopped':
    case 'down':
    case 'disabled':
    case 'false':
      bgColor = 'rgba(255, 68, 68, 0.1)';
      textColor = '#ff4444';
      borderColor = 'rgba(255, 68, 68, 0.3)';
      break;
    case 'pending':
    case 'starting':
    case 'stopping':
      bgColor = 'rgba(255, 193, 7, 0.1)';
      textColor = '#ffc107';
      borderColor = 'rgba(255, 193, 7, 0.3)';
      break;
    default:
      bgColor = 'rgba(158, 158, 158, 0.1)';
      textColor = '#9e9e9e';
      borderColor = 'rgba(158, 158, 158, 0.3)';
  }

  return {
    background: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    fontWeight: 500,
  };
});

const JellyfinStatistics = ({ apiBaseUrl, token, jellyfinUrl = 'https://jelly.raidnet.pl/', onSessionsCountChange, isTrueNASOffline }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [scanMessage, setScanMessage] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [scheduledTasks, setScheduledTasks] = useState([]);

  // Reset to Statistics tab when switching to mobile to avoid being on hidden Scheduled Tasks tab
  useEffect(() => {
    if (isMobile && activeTab === 2) {
      setActiveTab(0);
    }
  }, [isMobile, activeTab]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [activitySearch, setActivitySearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchDashboard = useCallback(async () => {
    if (isTrueNASOffline) {
      console.log('⚠️ Skipping Jellyfin dashboard fetch: TrueNAS is offline');
      setData(null);
      setScheduledTasks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const headers = { Authorization: `Bearer ${token}` };
      const [dashboardRes, foldersRes, tasksRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/api/jellyfin/dashboard`, { headers, timeout: 15000 }),
        axios.get(`${apiBaseUrl}/api/jellyfin/data-folder-sizes`, { headers, timeout: 60000 }).catch(() => ({ data: null })),
        axios.get(`${apiBaseUrl}/api/jellyfin/scheduled-tasks`, { headers, timeout: 15000 }).catch(() => ({ data: { tasks: [] } })),
      ]);
      setData({
        ...dashboardRes.data,
        data_folders: foldersRes.data,
      });
      setScheduledTasks(tasksRes.data?.tasks || []);
    } catch (err) {
      console.error('Jellyfin dashboard error:', err);
      setError(err.response?.data?.detail || 'Failed to load Jellyfin data. Check JELLYFIN_BASE_URL and JELLYFIN_API_KEY in .env');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token, isTrueNASOffline]);

  const handleScan = async () => {
    try {
      setScanning(true);
      setScanMessage(null);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${apiBaseUrl}/api/jellyfin/scan`, {}, { headers });
      if (response.data.success) {
        setScanMessage({ type: 'success', text: 'Library scan triggered!' });
      } else {
        setScanMessage({ type: 'error', text: response.data.message || 'Failed to trigger scan' });
      }
    } catch (err) {
      console.error('Jellyfin scan error:', err);
      setScanMessage({ type: 'error', text: err.response?.data?.detail || 'Error triggering scan' });
    } finally {
      setScanning(false);
      // Clear message after 5 seconds
      setTimeout(() => setScanMessage(null), 5000);
    }
  };

  useEffect(() => {
    // Only fetch if data is null or if we just came online
    if (!data && !isTrueNASOffline) {
      fetchDashboard();
    }
  }, [fetchDashboard, data, isTrueNASOffline]);

  const formatBytes = (bytes) => {
    if (bytes == null || bytes < 0) return '—';
    if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(2)} TB`;
    if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
    if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(2)} MB`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return dateStr;
    }
  };

  const formatJellyfinTicks = (ticks) => {
    if (!ticks) return 'Not scheduled';
    // Convert ticks to time (1 tick = 100 nanoseconds)
    const milliseconds = ticks / 10000;
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `Every ${hours} hour${hours > 1 ? 's' : ''}`;
    } else if (minutes > 0) {
      return `Every ${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      return `Every ${seconds} second${seconds > 1 ? 's' : ''}`;
    }
  };

  const formatTimeOfDayTicks = (ticks) => {
    if (!ticks) return null;
    // Convert ticks to time of day (1 tick = 100 nanoseconds)
    const milliseconds = ticks / 10000;
    const totalMinutes = Math.floor(milliseconds / 1000 / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const getJellyfinTaskStatus = (task) => {
    const state = (task.state || 'Idle').toLowerCase();
    if (state === 'running') return 'running';
    if (state === 'idle') return 'active';
    if (state === 'cancelling') return 'pending';
    return 'disabled';
  };

  const nowPlayingLabel = (session) => {
    const item = get(session, 'NowPlayingItem', 'nowPlayingItem');
    if (!item) return null;
    const name = get(item, 'Name', 'name');
    const series = get(item, 'SeriesName', 'seriesName');
    const type = get(item, 'Type', 'type');
    if (series) return `${series} · ${name || 'Episode'}`;
    return name || type || 'Playing';
  };

  const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
  const systemInfo = data?.system_info || data?.systemInfo || {};
  const itemCounts = data?.item_counts || data?.itemCounts || {};
  const activityLog = Array.isArray(data?.activity_log) ? data.activity_log : [];
  const recentlyAdded = Array.isArray(data?.recently_added) ? data.recently_added : [];
  const users = Array.isArray(data?.users) ? data.users : [];
  const serverVersion = get(systemInfo, 'Version', 'version') || '—';
  const movieCount = get(itemCounts, 'MovieCount', 'movieCount') ?? 0;
  const seriesCount = get(itemCounts, 'SeriesCount', 'seriesCount') ?? 0;
  const episodeCount = get(itemCounts, 'EpisodeCount', 'episodeCount') ?? 0;

  useEffect(() => {
    if (!onSessionsCountChange) return;
    onSessionsCountChange(sessions.length);
  }, [sessions.length, onSessionsCountChange]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          borderRadius: '16px',
          backgroundColor: 'rgba(211, 47, 47, 0.1)',
          color: '#ff8a80',
          border: '1px solid rgba(211, 47, 47, 0.2)',
          '& .MuiAlert-icon': { color: '#ff8a80' },
        }}
      >
        {error}
      </Alert>
    );
  }

  const userNameById = (userId) => {
    if (!userId) return '—';
    const u = users.find((x) => String(get(x, 'Id', 'id') || '') === String(userId));
    return u ? (get(u, 'Name', 'name') || get(u, 'UserName', 'userName') || '—') : '—';
  };

  const dataFolders = data?.data_folders?.folders ?? [];
  const totalFolderBytes = data?.data_folders?.total_bytes ?? 0;
  const totalFileCount = data?.data_folders?.total_count ?? 0;
  
  const disk1 = dataFolders[0] || {};
  const disk2 = dataFolders[1] || {};
  
  const getUsageColor = (percent) => {
    if (percent > 90) return '#ff4444';
    if (percent > 75) return '#ffc107';
    return '#00ff88';
  };

  const sortedActivity = [...activityLog].sort((a, b) => {
    const da = new Date(get(a, 'Date', 'date') || 0).getTime();
    const db = new Date(get(b, 'Date', 'date') || 0).getTime();
    return db - da;
  });

  const renderDiskCard = (disk, colorTheme, iconSrc) => {
    const usage = disk.usage || {};
    const percent = usage.percent || 0;
    const isOnline = disk.size_bytes != null;
    
    return (
      <JellyfinDiskCard>
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src={iconSrc}
                sx={{
                  width: 42,
                  height: 42,
                  filter: `drop-shadow(0 0 10px ${colorTheme === 'blue' ? '#00d4ff' : colorTheme === 'orange' ? '#ff9800' : '#00ff88'}40)`,
                }}
              />
              <Box>
                <CardTitle sx={{ fontSize: '1.1rem', mb: 0.5 }}>{disk.label || 'Unknown'}</CardTitle>
                <DiskStatusChip
                  status={isOnline}
                  label={isOnline ? 'Online' : 'Offline'}
                  icon={isOnline ? <OnlineIcon /> : <OfflineIcon />}
                  size="small"
                />
              </Box>
            </Box>
            {disk.file_count != null && (
              <Chip
                label={`${disk.file_count.toLocaleString()} files · ${formatBytes(disk.size_bytes)}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              />
            )}
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <SecondaryText sx={{ fontSize: '0.75rem', fontWeight: 600 }}>PARTITION USAGE</SecondaryText>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: getUsageColor(percent) }}>
                {percent}%
              </Typography>
            </Box>
            <UsageProgress variant="determinate" value={percent} color_theme={colorTheme} />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <CaptionText sx={{ mb: 0.5 }}>USED</CaptionText>
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                {usage.used ? formatBytes(usage.used) : formatBytes(disk.size_bytes)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <CaptionText sx={{ mb: 0.5 }}>TOTAL</CaptionText>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, fontSize: '0.9rem' }}>
                {usage.total ? formatBytes(usage.total) : '—'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <CaptionText sx={{ mb: 0.5 }}>FREE</CaptionText>
              <Typography sx={{ color: getUsageColor(percent), fontWeight: 600, fontSize: '0.9rem' }}>
                {usage.free ? formatBytes(usage.free) : '—'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </JellyfinDiskCard>
    );
  };

  return (
    <Box sx={{ animation: `${fadeInUp} 0.6s ease-out` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, rgba(170, 92, 195, 0.15), rgba(0, 212, 255, 0.15))',
              border: '1px solid rgba(170, 92, 195, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src="/jellyfin.png"
              alt="Jellyfin"
              sx={{ width: 36, height: 36, objectFit: 'contain' }}
            />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.5px' }}>
              Jellyfin
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <OpenInNew sx={{ fontSize: 14 }} />{' '}
              <Link
                href={jellyfinUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', '&:hover': { color: '#00d4ff' } }}
              >
                {jellyfinUrl}
              </Link>
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={fetchDashboard}
            disabled={loading}
            sx={{
              color: '#00d4ff',
              background: 'rgba(0, 212, 255, 0.1)',
              border: '1px solid rgba(0, 212, 255, 0.1)',
              '&:hover': { background: 'rgba(0, 212, 255, 0.2)', borderColor: 'rgba(0, 212, 255, 0.3)' },
              '&.Mui-disabled': { background: 'rgba(0, 212, 255, 0.05)' },
            }}
          >
            <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tabs for Statistics and Scheduled Tasks */}
      <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              background: (theme) => theme.palette.primary.main,
              height: '3px'
            },
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.6)',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              '&.Mui-selected': { color: (theme) => theme.palette.primary.main },
              '& svg': {
                color: 'inherit'
              }
            }
          }}
        >
          <Tab icon={<BarChart3 size={18} />} iconPosition="start" label="Statistics" />
          <Tab icon={<History size={18} />} iconPosition="start" label="History" />
          {!isMobile && <Tab icon={<Clock size={18} />} iconPosition="start" label="Scheduled Tasks" />}
        </Tabs>
      </Box>

      {/* Action buttons — stacked full-width on mobile for better fit */}
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1.5,
            '& .MuiButton-root': {
              width: '100%',
              justifyContent: 'center',
              padding: '10px 16px',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: 600,
            },
          },
        })}
      >
        {scanMessage && (
          <Typography
            variant="caption"
            sx={(theme) => ({
              color: scanMessage.type === 'success' ? '#4caf50' : '#f44336',
              mr: 1,
              animation: `${fadeInUp} 0.3s ease-out`,
              [theme.breakpoints.down('sm')]: { width: '100%', textAlign: 'center', mb: 0 },
            })}
          >
            {scanMessage.text}
          </Typography>
        )}
        
        <Button
          onClick={handleScan}
          disabled={scanning}
          variant="contained"
          startIcon={scanning ? <CircularProgress size={18} color="inherit" /> : <Search />}
          sx={{
            background: 'rgba(255, 152, 0, 0.1)',
            color: '#ff9800',
            border: '1px solid rgba(255, 152, 0, 0.3)',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 16px',
            '&:hover': { 
              background: 'rgba(255, 152, 0, 0.2)',
              borderColor: '#ff9800'
            },
            '&.Mui-disabled': {
              background: 'rgba(255, 255, 255, 0.05)',
              color: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          {scanning ? 'Scanning...' : 'Scan Libraries'}
        </Button>
      </Box>

      {/* Statistics Tab */}
      {activeTab === 0 && (
        <Box>

      {/* Mobile: compact horizontal scroll for quick stats */}
      <SummaryScrollRow>
        <CompactSummaryCard>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#ff9800', mb: 0.5 }}>
            <Devices sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.6rem' }}>Sessions</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{sessions.length}</Typography>
        </CompactSummaryCard>
        <CompactSummaryCard>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0, 212, 255, 0.15)', color: '#00d4ff', mb: 0.5 }}>
            <PlayArrow sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.6rem' }}>Version</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.8rem' }}>{serverVersion}</Typography>
        </CompactSummaryCard>
        <CompactSummaryCard>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(233, 30, 99, 0.15)', color: '#e91e63', mb: 0.5 }}>
            <Movie sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.6rem' }}>Movies</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{Number(movieCount).toLocaleString()}</Typography>
        </CompactSummaryCard>
        <CompactSummaryCard>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(103, 58, 183, 0.15)', color: '#673ab7', mb: 0.5 }}>
            <Tv sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.6rem' }}>Series</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{Number(seriesCount).toLocaleString()}</Typography>
        </CompactSummaryCard>
        <CompactSummaryCard>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0, 150, 136, 0.15)', color: '#009688', mb: 0.5 }}>
            <PlayArrow sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.6rem' }}>Episodes</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{Number(episodeCount).toLocaleString()}</Typography>
        </CompactSummaryCard>
      </SummaryScrollRow>

      {/* Mobile: horizontal scroll for disk cards + total storage */}
      <DiskCardsScrollRow sx={{ mt: 2 }}>
        {renderDiskCard(disk1, 'blue', '/ssd.png')}
        {renderDiskCard(disk2, 'orange', '/nvme.png')}
        <JellyfinDiskCard sx={{ '&::before': { background: 'linear-gradient(90deg, #00ff88, #00d4ff)' }, minWidth: 280 }}>
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <IconWrapper sx={{ background: 'rgba(0, 255, 136, 0.1)', color: '#00ff88', border: '1px solid rgba(0, 255, 136, 0.2)', width: 48, height: 48 }}>
                <HardDrive size={24} />
              </IconWrapper>
              <Box>
                <CardTitle sx={{ fontSize: '1.1rem' }}>Total Storage</CardTitle>
                <SecondaryText sx={{ fontSize: '0.8rem' }}>Aggregated Media Data</SecondaryText>
              </Box>
            </Box>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <CaptionText>TOTAL USED SIZE</CaptionText>
                <Typography sx={{ color: '#00ff88', fontWeight: 700 }}>{formatBytes(totalFolderBytes)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <CaptionText>TOTAL FILES</CaptionText>
                <Typography sx={{ color: '#fff', fontWeight: 700 }}>{totalFileCount.toLocaleString()}</Typography>
              </Box>
            </Box>
            <PrimaryButton fullWidth startIcon={<Refresh />} onClick={fetchDashboard} disabled={loading} sx={{ borderRadius: '12px', height: 44 }}>
              Refresh Data
            </PrimaryButton>
          </Box>
        </JellyfinDiskCard>
      </DiskCardsScrollRow>

      {/* Desktop: grid for quick stats + disk cards */}
      <Grid container spacing={3} sx={{ display: { xs: 'none', sm: 'flex' } }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard>
            <IconWrapper sx={{ background: 'rgba(255, 152, 0, 0.1)', color: '#ff9800', border: '1px solid rgba(255, 152, 0, 0.2)', width: 44, height: 44, mr: 2 }}>
              <Devices />
            </IconWrapper>
            <Box>
              <CaptionText>Sessions</CaptionText>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>{sessions.length}</Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard>
            <IconWrapper sx={{ background: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff', border: '1px solid rgba(0, 212, 255, 0.2)', width: 44, height: 44, mr: 2 }}>
              <PlayArrow />
            </IconWrapper>
            <Box>
              <CaptionText>Version</CaptionText>
              <Typography sx={{ fontWeight: 600, color: '#fff', fontSize: '0.95rem' }}>{serverVersion}</Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={4} md={2.4}>
          <SummaryCard>
            <IconWrapper sx={{ background: 'rgba(233, 30, 99, 0.1)', color: '#e91e63', border: '1px solid rgba(233, 30, 99, 0.2)', width: 44, height: 44, mr: 2 }}>
              <Movie />
            </IconWrapper>
            <Box>
              <CaptionText>Movies</CaptionText>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>{Number(movieCount).toLocaleString()}</Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={4} md={2.4}>
          <SummaryCard>
            <IconWrapper sx={{ background: 'rgba(103, 58, 183, 0.1)', color: '#673ab7', border: '1px solid rgba(103, 58, 183, 0.2)', width: 44, height: 44, mr: 2 }}>
              <Tv />
            </IconWrapper>
            <Box>
              <CaptionText>Series</CaptionText>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>{Number(seriesCount).toLocaleString()}</Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={4} md={2.4}>
          <SummaryCard>
            <IconWrapper sx={{ background: 'rgba(0, 150, 136, 0.1)', color: '#009688', border: '1px solid rgba(0, 150, 136, 0.2)', width: 44, height: 44, mr: 2 }}>
              <PlayArrow />
            </IconWrapper>
            <Box>
              <CaptionText>Episodes</CaptionText>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>{Number(episodeCount).toLocaleString()}</Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} md={4.5}>
          {renderDiskCard(disk1, 'blue', '/ssd.png')}
        </Grid>
        <Grid item xs={12} md={4.5}>
          {renderDiskCard(disk2, 'orange', '/nvme.png')}
        </Grid>
        <Grid item xs={12} md={3}>
          <JellyfinDiskCard sx={{ '&::before': { background: 'linear-gradient(90deg, #00ff88, #00d4ff)' } }}>
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconWrapper sx={{ background: 'rgba(0, 255, 136, 0.1)', color: '#00ff88', border: '1px solid rgba(0, 255, 136, 0.2)', width: 48, height: 48 }}>
                  <HardDrive size={24} />
                </IconWrapper>
                <Box>
                  <CardTitle sx={{ fontSize: '1.1rem' }}>Total Storage</CardTitle>
                  <SecondaryText sx={{ fontSize: '0.8rem' }}>Aggregated Media Data</SecondaryText>
                </Box>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <CaptionText>TOTAL USED SIZE</CaptionText>
                  <Typography sx={{ color: '#00ff88', fontWeight: 700 }}>{formatBytes(totalFolderBytes)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <CaptionText>TOTAL FILES</CaptionText>
                  <Typography sx={{ color: '#fff', fontWeight: 700 }}>{totalFileCount.toLocaleString()}</Typography>
                </Box>
              </Box>
              <PrimaryButton fullWidth startIcon={<Refresh />} onClick={fetchDashboard} disabled={loading} sx={{ borderRadius: '12px', height: 44 }}>
                Refresh Data
              </PrimaryButton>
            </Box>
          </JellyfinDiskCard>
        </Grid>
      </Grid>

      {recentlyAdded.length > 0 && (
        <>
          <SectionTitle sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <History size={20} style={{ color: '#00d4ff' }} />
            Recently Added
          </SectionTitle>
          {/* Mobile: horizontal scroll for recently added */}
          <RecentlyAddedScrollRow>
            {recentlyAdded.map((item) => {
              const id = get(item, 'Id', 'id');
              const name = get(item, 'Name', 'name');
              const year = get(item, 'ProductionYear', 'productionYear');
              const type = get(item, 'Type', 'type');
              const imageTag = (item.ImageTags || item.imageTags || {}).Primary;
              const baseUrl = jellyfinUrl.replace(/\/$/, '');
              const imageUrl = imageTag 
                ? `${baseUrl}/Items/${id}/Images/Primary?fillHeight=320&fillWidth=213&quality=90&tag=${imageTag}`
                : null;
              return (
                <Link 
                  key={id}
                  href={`${baseUrl}/web/index.html#!/details?id=${id}`} 
                  target="_blank" 
                  sx={{ textDecoration: 'none' }}
                >
                  <MediaCard>
                    {imageUrl ? (
                      <Box component="img" src={imageUrl} alt={name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.05)' }}>
                        {type === 'Series' ? <Tv size={48} opacity={0.3} /> : <Movie size={48} opacity={0.3} />}
                      </Box>
                    )}
                    <MediaOverlay className="media-overlay">
                      <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{year || type}</Typography>
                        <Chip label={type === 'Series' ? 'TV' : 'Movie'} size="small" sx={{ height: 16, fontSize: '0.6rem', backgroundColor: type === 'Series' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(0, 212, 255, 0.2)', color: type === 'Series' ? '#ce93d8' : '#81d4fa', border: 'none' }} />
                      </Box>
                    </MediaOverlay>
                  </MediaCard>
                </Link>
              );
            })}
          </RecentlyAddedScrollRow>
          {/* Desktop: grid for recently added */}
          <Grid container spacing={2} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            {recentlyAdded.map((item) => {
              const id = get(item, 'Id', 'id');
              const name = get(item, 'Name', 'name');
              const year = get(item, 'ProductionYear', 'productionYear');
              const type = get(item, 'Type', 'type');
              const imageTag = (item.ImageTags || item.imageTags || {}).Primary;
              const baseUrl = jellyfinUrl.replace(/\/$/, '');
              const imageUrl = imageTag 
                ? `${baseUrl}/Items/${id}/Images/Primary?fillHeight=320&fillWidth=213&quality=90&tag=${imageTag}`
                : null;
              return (
                <Grid item xs={6} sm={4} md={2.4} key={id}>
                  <Link href={`${baseUrl}/web/index.html#!/details?id=${id}`} target="_blank" sx={{ textDecoration: 'none' }}>
                    <MediaCard>
                      {imageUrl ? (
                        <Box component="img" src={imageUrl} alt={name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.05)' }}>
                          {type === 'Series' ? <Tv size={48} opacity={0.3} /> : <Movie size={48} opacity={0.3} />}
                        </Box>
                      )}
                      <MediaOverlay className="media-overlay">
                        <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{year || type}</Typography>
                          <Chip label={type === 'Series' ? 'TV' : 'Movie'} size="small" sx={{ height: 16, fontSize: '0.6rem', backgroundColor: type === 'Series' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(0, 212, 255, 0.2)', color: type === 'Series' ? '#ce93d8' : '#81d4fa', border: 'none' }} />
                        </Box>
                      </MediaOverlay>
                    </MediaCard>
                  </Link>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {sessions.length > 0 && (
        <>
          <SectionTitle sx={{ mt: 4 }}>Connected users & devices</SectionTitle>
          {/* Mobile: horizontal scroll of session cards */}
          <SessionCardsScrollRow>
            {sessions.map((s) => {
              const userName = get(s, 'UserName', 'userName') || '—';
              const device = get(s, 'DeviceName', 'deviceName') || get(s, 'Client', 'client') || '—';
              const client = get(s, 'Client', 'client');
              const appVersion = get(s, 'ApplicationVersion', 'applicationVersion');
              const playing = nowPlayingLabel(s);
              const lastActivity = get(s, 'LastActivityDate', 'lastActivityDate');
              return (
                <GlassmorphicCard key={get(s, 'Id', 'id') || userName + device} sx={{ p: 2, minWidth: 260 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(0, 212, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00d4ff' }}>
                      <Devices sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff' }}>{userName}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mb: 1 }}>{device}</Typography>
                  {(client || appVersion) && (
                    <Chip size="small" label={[client, appVersion].filter(Boolean).join(' ')} sx={{ fontSize: '0.65rem', height: 20, backgroundColor: 'rgba(255,255,255,0.06)', mb: 1 }} />
                  )}
                  {playing ? (
                    <Chip size="small" icon={<PlayArrow sx={{ fontSize: '0.9rem !important' }} />} label={playing} sx={{ backgroundColor: 'rgba(0, 212, 255, 0.12)', color: '#00d4ff', border: '1px solid rgba(0, 212, 255, 0.25)', fontSize: '0.7rem', mb: 1 }} />
                  ) : (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1 }}>Not playing</Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{formatDate(lastActivity)}</Typography>
                </GlassmorphicCard>
              );
            })}
          </SessionCardsScrollRow>
          {/* Desktop: table */}
          <StyledTableContainer sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Device / Client</TableCell>
                  <TableCell>Now playing</TableCell>
                  <TableCell>Last activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((s) => {
                  const userName = get(s, 'UserName', 'userName') || '—';
                  const device = get(s, 'DeviceName', 'deviceName') || get(s, 'Client', 'client') || '—';
                  const client = get(s, 'Client', 'client');
                  const appVersion = get(s, 'ApplicationVersion', 'applicationVersion');
                  const playing = nowPlayingLabel(s);
                  const lastActivity = get(s, 'LastActivityDate', 'lastActivityDate');
                  return (
                    <TableRow key={get(s, 'Id', 'id') || userName + device}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{userName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2">{device}</Typography>
                          {(client || appVersion) && (
                            <Chip size="small" label={[client, appVersion].filter(Boolean).join(' ')} sx={{ fontSize: '0.7rem', height: 22, backgroundColor: 'rgba(255,255,255,0.08)' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {playing ? (
                          <Chip size="small" icon={<PlayArrow sx={{ fontSize: '1rem !important' }} />} label={playing} sx={{ backgroundColor: 'rgba(0, 212, 255, 0.15)', color: '#00d4ff', border: '1px solid rgba(0, 212, 255, 0.3)' }} />
                        ) : (
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{formatDate(lastActivity)}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </>
      )}

      {sessions.length === 0 && !loading && (
        <Box sx={{ mt: 3, py: 3, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
          <Typography variant="body2">No active sessions right now.</Typography>
        </Box>
      )}
        </Box>
      )}

      {/* History Tab */}
      {activeTab === 1 && (
        <Box sx={{ animation: `${fadeInUp} 0.4s ease-out` }}>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2, 
            mb: 3, 
            p: isMobile ? 1.5 : 2, 
            borderRadius: isMobile ? '12px' : '16px', 
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'stretch'
          }}>
            <Typography variant="subtitle2" sx={{ color: '#00d4ff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isMobile && 'FILTERS:'}
            </Typography>
            
            <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 200, flex: isMobile ? 1 : 'none' }}>
              <InputLabel id="user-filter-label" sx={{ color: 'rgba(255,255,255,0.6)' }}>User</InputLabel>
              <Select
                labelId="user-filter-label"
                value={selectedUserId}
                label="User"
                onChange={(e) => {
                  setSelectedUserId(e.target.value);
                  setCurrentPage(1);
                }}
                sx={{ 
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                }}
              >
                <MenuItem value="all">All Users</MenuItem>
                {users.map(u => {
                  const id = get(u, 'Id', 'id');
                  const name = get(u, 'Name', 'name') || get(u, 'UserName', 'userName');
                  return (
                    <MenuItem key={id} value={id}>{name}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="Search activity..."
              value={activitySearch}
              onChange={(e) => {
                setActivitySearch(e.target.value);
                setCurrentPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                flexGrow: 1,
                flex: isMobile ? '1 1 100%' : 1,
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                }
              }}
            />

            <Button 
              size="small" 
              onClick={() => {
                setSelectedUserId('all');
                setActivitySearch('');
              }}
              sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', minWidth: 'auto' }}
            >
              Reset
            </Button>
          </Box>

          {(() => {
            const filtered = sortedActivity.filter(entry => {
              const userId = get(entry, 'UserId', 'userId');
              const overview = (get(entry, 'Overview', 'overview') || get(entry, 'ShortOverview', 'shortOverview') || get(entry, 'Name', 'name') || '').toLowerCase();
              const matchesUser = selectedUserId === 'all' || String(userId) === String(selectedUserId);
              const matchesSearch = overview.includes(activitySearch.toLowerCase());
              return matchesUser && matchesSearch;
            });
            
            const pageCount = Math.ceil(filtered.length / itemsPerPage);
            const paginatedData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

            return filtered.length > 0 ? (
              <>
                {isMobile ? (
                  // Mobile card view
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {paginatedData.map((entry) => {
                      const date = get(entry, 'Date', 'date');
                      const userId = get(entry, 'UserId', 'userId');
                      const overview = get(entry, 'Overview', 'overview') || get(entry, 'ShortOverview', 'shortOverview') || get(entry, 'Name', 'name') || '—';
                      return (
                        <Box key={get(entry, 'Id', 'id') || date + userId} sx={{ 
                          p: 1.5, 
                          borderRadius: '12px', 
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.08)'
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                              {formatDate(date)}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#00ff88', fontSize: '0.7rem' }}>
                              {userNameById(userId)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                            {overview}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  // Desktop table view
                  <StyledTableContainer sx={{ mt: 0 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>When</TableCell>
                          <TableCell>User</TableCell>
                          <TableCell>Activity</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedData.map((entry) => {
                          const date = get(entry, 'Date', 'date');
                          const userId = get(entry, 'UserId', 'userId');
                          const overview = get(entry, 'Overview', 'overview') || get(entry, 'ShortOverview', 'shortOverview') || get(entry, 'Name', 'name') || '—';
                          return (
                            <TableRow key={get(entry, 'Id', 'id') || date + userId}>
                              <TableCell sx={{ width: '180px' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                                  {formatDate(date)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: '150px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#00ff88' }}>
                                    {userNameById(userId)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                                  {overview}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </StyledTableContainer>
                )}
                
                {pageCount > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                    <Pagination 
                      count={pageCount} 
                      page={currentPage} 
                      onChange={(e, v) => setCurrentPage(v)}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(0, 212, 255, 0.2)',
                            color: '#00d4ff',
                            border: '1px solid rgba(0, 212, 255, 0.3)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ mt: 2, py: 8, textAlign: 'center', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.02)', borderRadius: '24px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                <History sx={{ fontSize: 48, opacity: 0.2, mb: 2 }} />
                <Typography variant="body1">No activity matches your filters.</Typography>
                <Typography variant="caption">Try adjusting your selection or search query.</Typography>
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Scheduled Tasks Tab */}
      {activeTab === 2 && (
        <Box>
          {scheduledTasks.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 6,
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              <Typography variant="body1">
                {loading ? 'Loading Jellyfin scheduled tasks...' : 'No Jellyfin scheduled tasks found'}
              </Typography>
            </Box>
          ) : (
            <StyledTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Task Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Schedule</TableCell>
                    <TableCell>Last Run</TableCell>
                    <TableCell>State</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scheduledTasks.filter(task => !task.is_hidden).map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PlayCircle sx={{ color: '#9c27b0', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500 }}>
                              {task.name}
                            </Typography>
                            {task.description && (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                                {task.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          {task.category || 'General'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {task.triggers && task.triggers.length > 0 ? (
                          <Box>
                            {task.triggers.map((trigger, idx) => (
                              <Typography key={idx} variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                {trigger.type === 'IntervalTrigger' && formatJellyfinTicks(trigger.interval_ticks)}
                                {trigger.type === 'DailyTrigger' && `Daily at ${formatTimeOfDayTicks(trigger.time_of_day_ticks)}`}
                                {trigger.type !== 'IntervalTrigger' && trigger.type !== 'DailyTrigger' && trigger.type}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                            Manual only
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          {formatDate(task.last_run_time)}
                        </Typography>
                        {task.last_execution_result && (
                          <Typography variant="caption" sx={{
                            color: task.last_execution_result.status === 'Completed' ? '#00ff88' : '#ff4444',
                            display: 'block'
                          }}>
                            {task.last_execution_result.status}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          label={task.state || 'Idle'}
                          status={getJellyfinTaskStatus(task)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default JellyfinStatistics;
